import React, { useEffect, useState } from 'react';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Warehouse, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function GodownMasterPage() {
  const [godowns, setGodowns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editGodown, setEditGodown] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', description: '', location: '' });

  useEffect(() => { loadGodowns(); }, []);

  const loadGodowns = async () => {
    try {
      const res = await inventoryAPI.listGodowns();
      setGodowns(res.data);
    } catch (err) {
      toast.error('Failed to load godowns');
    } finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditGodown(null);
    setForm({ name: '', code: '', description: '', location: '' });
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setEditGodown(g);
    setForm({ name: g.name, code: g.code, description: g.description || '', location: g.location || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.code) { toast.error('Name and code are required'); return; }
    try {
      if (editGodown) {
        await inventoryAPI.updateGodown(editGodown.id, form);
        toast.success('Godown updated');
      } else {
        await inventoryAPI.createGodown(form);
        toast.success('Godown created');
      }
      setDialogOpen(false);
      loadGodowns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save godown');
    }
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete godown "${g.name}"?`)) return;
    try {
      await inventoryAPI.deleteGodown(g.id);
      toast.success('Godown deleted');
      loadGodowns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-4" data-testid="godown-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Godown Master</h1>
          <p className="text-xs text-muted-foreground">{godowns.length} storage locations</p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="create-godown-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Godown
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? (
          <p className="col-span-full text-center text-xs text-muted-foreground py-8">Loading...</p>
        ) : godowns.length === 0 ? (
          <p className="col-span-full text-center text-xs text-muted-foreground py-8">No godowns configured</p>
        ) : godowns.map(g => (
          <Card key={g.id} className="hover:shadow-sm transition-shadow" data-testid={`godown-card-${g.code}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-md bg-cyan-50">
                    <Warehouse className="w-4 h-4 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium">{g.name}</h3>
                    <p className="text-[10px] text-muted-foreground font-mono">{g.code}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(g)} data-testid={`edit-godown-${g.code}`}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDelete(g)} data-testid={`delete-godown-${g.code}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {g.description && <p className="text-xs text-muted-foreground mt-2">{g.description}</p>}
              {g.location && <p className="text-[10px] text-muted-foreground mt-1">Location: {g.location}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editGodown ? 'Edit Godown' : 'New Godown'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                placeholder="e.g. Stores" className="h-8 text-xs" data-testid="godown-name-input" />
            </div>
            <div>
              <Label className="text-xs">Code *</Label>
              <Input value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value.toUpperCase()}))}
                placeholder="e.g. STORE" className="h-8 text-xs font-mono" disabled={!!editGodown} data-testid="godown-code-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                placeholder="Brief description" className="h-8 text-xs" data-testid="godown-desc-input" />
            </div>
            <div>
              <Label className="text-xs">Physical Location</Label>
              <Input value={form.location} onChange={e => setForm(f => ({...f, location: e.target.value}))}
                placeholder="e.g. Building A, Floor 1" className="h-8 text-xs" data-testid="godown-location-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-godown-btn">{editGodown ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
